import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, MenuIcon, PlusIcon, XIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { mobileQuickNav } from '../../data/adminNav';
import { buttonClasses } from '../ui/Button';
import { AdminSidebar } from './shell/AdminSidebar';
import { AdminLogo } from './shell/AdminLogo';
import { GlobalSearch } from './shell/GlobalSearch';
import { NotificationsMenu } from './shell/NotificationsMenu';
import { ProfileMenu } from './shell/ProfileMenu';
import { ThemeToggle } from './shell/ThemeToggle';
import { adminThemeClass, useAdminTheme } from '../../hooks/useAdminTheme';
import { TimerProvider, useTimer } from '../../contexts/TimerContext';
import { TimerBar } from './timer/TimerBar';
import { SyncStatusBar } from './shell/SyncStatusBar';
import { applyAdminAppMeta, isStandalone, registerAdminServiceWorker } from '../../utils/pwa';
const ease = [0.23, 1, 0.32, 1] as const;

/** Sættes første gang admin åbnes i denne visning. */
let openedOnToday = false;
export function AdminShell() {
  return <TimerProvider data-id="element-49" data-mp="3aspheum5gthy">
      <AdminShellLayout data-id="element-50" data-mp="3at91agvd1rym" />
    </TimerProvider>;
}
function AdminShellLayout() {
  const {
    logout,
    dataError,
    rulesError,
    enquiries
  } = useAdminData();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    theme
  } = useAdminTheme();
  const {
    timerError
  } = useTimer();
  const newEnquiries = enquiries.filter(item => item.status === 'Ny henvendelse').length;
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // App-manifest og service worker gælder kun admin — forsiden er uændret.
  useEffect(() => {
    const cleanup = applyAdminAppMeta();
    void registerAdminServiceWorker();
    return cleanup;
  }, []);

  // Den installerede app og mobilen åbner på I dag; computeren bliver på Overblik.
  useEffect(() => {
    if (openedOnToday) return;
    openedOnToday = true;
    const mobile = window.matchMedia('(max-width: 1023px)').matches;
    if (location.pathname === '/admin' && (mobile || isStandalone())) {
      navigate('/admin/i-dag', {
        replace: true
      });
    }
    // Kun ved åbning af appen — ikke når man senere vælger Overblik.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function handleLogout() {
    await logout();
    navigate('/admin/login');
  }
  return <div className={`${adminThemeClass(theme)} flex min-h-screen w-full bg-canvas font-sans text-ink`} data-id="element-51" data-mp="x2sm4835hwux">
      {/* Fast sidebar på desktop */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-line bg-canvas lg:block" data-id="element-52" data-mp="172jg88nsu9uh">
        <AdminSidebar newEnquiries={newEnquiries} onLogout={handleLogout} data-id="element-53" data-mp="33vmr4qfs3n6i" />
      </aside>

      {/* Skuffe-menu på mobil */}
      <AnimatePresence data-id="element-54" data-mp="38uzb93f9mg5e">
        {drawerOpen && <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-id="element-55" data-mp="1pz1terg1wr4c">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} data-id="element-56" data-mp="1l8lf754ep1gx" />
            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.26,
          ease
        }} className="absolute inset-y-0 left-0 flex w-[min(19rem,85vw)] flex-col border-r border-line bg-canvas" data-id="element-57" data-mp="28yfrnlktco0n">
              <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Luk menu" className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft hover:bg-white hover:text-ink" data-id="element-58" data-mp="1uyy0izk1oyo4">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-59" data-mp="27ew19f9cxmhx" />
              </button>
              <div className="min-h-0 flex-1" data-id="element-60" data-mp="2c7fnf0m41pyc">
                <AdminSidebar newEnquiries={newEnquiries} onNavigate={() => setDrawerOpen(false)} onLogout={handleLogout} header={<div className="px-4 pb-2 pt-16" data-id="element-62" data-mp="23bpzajta2hh9">
                      <GlobalSearch onNavigate={() => setDrawerOpen(false)} data-id="element-63" data-mp="2r4zmxs08msx4" />
                    </div>} data-id="element-61" data-mp="2c88njiqkdh7i" />
              </div>
            </motion.aside>
          </div>}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-64" data-mp="2h7l81wtyqc6x">
        <header className="sticky top-0 z-40 border-b border-line bg-canvas/90 pt-[env(safe-area-inset-top)] backdrop-blur" data-id="element-65" data-mp="2m3ipy7kvjqlt">
          <TimerBar data-id="element-66" data-mp="15oqypxzgspe3" />
          <div className="flex h-16 items-center gap-3 px-4 md:px-8" data-id="element-67" data-mp="15soimltv9kz1">
            <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Åbn menu" className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-soft hover:bg-white hover:text-ink lg:hidden" data-id="element-68" data-mp="1aqzq4pwovje3">
              <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-69" data-mp="1c5amoiwzq55z" />
            </button>
            <div className="lg:hidden" data-id="element-70" data-mp="178k67uh9x7k1">
              <AdminLogo data-id="element-71" data-mp="175efkaszxxm7" />
            </div>

            <div className="hidden max-w-xl flex-1 md:block" data-id="element-72" data-mp="jhen96g1dz5i">
              <GlobalSearch data-id="element-73" data-mp="jfk3vwkeyf3j" />
            </div>

            <div className="ml-auto flex items-center gap-1.5 md:gap-3" data-id="element-74" data-mp="fhcnx3xrxhte">
              <ThemeToggle data-id="element-75" data-mp="t7g5vipmepd1" />
              <NotificationsMenu data-id="element-76" data-mp="t8rga1dkmvd4" />
              <span className="hidden h-6 w-px bg-line md:block" aria-hidden="true" data-id="element-77" data-mp="t9stoci27ty0" />
              <ProfileMenu onLogout={handleLogout} data-id="element-78" data-mp="ofompt8snlwx" />
              <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md', 'hidden rounded-xl font-semibold sm:inline-flex')} data-id="element-79" data-mp="qwz7f65frqh">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-80" data-mp="3rx6u5zknmqwt" />
                Ny sag
              </Link>
              <Link to="/admin/sager/ny" aria-label="Ny sag" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-white transition-colors duration-150 hover:bg-accent-deep sm:hidden" data-id="element-81" data-mp="1vubjdgwp95ft">
                <PlusIcon className="h-5 w-5" aria-hidden="true" data-id="element-82" data-mp="2ai6e12u9pjvj" />
              </Link>
            </div>
          </div>
        </header>

        <SyncStatusBar data-id="element-83" data-mp="1cy27n099sqyz" />

        {timerError && <p role="alert" className="flex items-start gap-2 border-b border-danger/30 bg-danger/10 px-5 py-3 text-sm text-danger md:px-8" data-id="element-84" data-mp="1hvvc9ouxdcx5">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-85" data-mp="1mvzwnpyyersn" />
            Timer: {timerError}
          </p>}

        {dataError && <p role="alert" className="flex items-start gap-2 border-b border-danger/30 bg-danger/10 px-5 py-3 text-sm text-danger md:px-8" data-id="element-86" data-mp="2y8agh6vpzdky">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-87" data-mp="qnk20f2cw8nt" />
            {dataError}
          </p>}

        {rulesError && <p role="status" className="flex items-start gap-2 border-b border-line bg-accent-soft px-5 py-3 text-sm text-accent-deep md:px-8" data-id="element-88" data-mp="mo2vrjf3v7hl">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-89" data-mp="cvncmis2ocv4" />
            {rulesError}
          </p>}

        <main className="min-w-0 flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-0" data-id="element-90" data-mp="2xpqq1xoqs5o">
          <Outlet data-id="element-91" data-mp="3v1ijjw28biu9" />
        </main>

        {/* Hurtig adgang på mobil */}
        <nav aria-label="Hurtig navigation" className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-line bg-canvas/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" data-id="element-92" data-mp="3v1s9xkr6rs0h">
          {mobileQuickNav.map(item => <NavLink key={item.to} to={item.to} end={item.end} className={({
          isActive
        }) => `relative flex flex-1 flex-col items-center gap-1 py-3 text-[11px] transition-colors duration-150 ease-smooth ${isActive ? 'text-accent' : 'text-ink-muted'}`} data-id="element-93" data-mp="1oc3x4qihhjou">
              <item.icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-94" data-mp="se85h2ki4fbw" />
              {item.label}
              {item.showEnquiryBadge && newEnquiries > 0 && <span className="absolute right-1/4 top-2 h-2 w-2 rounded-full bg-danger" aria-hidden="true" data-id="element-95" data-mp="igu1w3jofl10" />}
            </NavLink>)}
          <button type="button" onClick={() => setDrawerOpen(true)} className="flex flex-1 flex-col items-center gap-1 py-3 text-[11px] text-ink-muted" data-id="element-96" data-mp="4ilybcs4xlgv">
            <MenuIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-97" data-mp="1yvlmhuakznzr" />
            Menu
          </button>
        </nav>
      </div>
    </div>;
}